<script setup lang="ts">
import { DialogContent, DialogRoot, DialogTitle } from 'radix-vue';
import { Autocomplete } from '@pkg/ui/components/ui/autocomplete';
import { useIsDesktop, useIsTablet } from '@pkg/ui/composables/useIsDesktop';
import { cn } from '@pkg/ui/lib/utils';
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import { THEME } from '@pkg/config/misc/theme';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import type { Origin } from '@pkg/types/surpriseBag';
import { resolveComponent, inject, ref, computed, watch } from 'vue';
import type { Ref } from 'vue';
import NavSubmenuList from './NavSubmenuList.vue';
import HeaderActions from './HeaderActions.vue';
import LanguagePicker from './LanguagePicker.vue';
import ChevronRightIcon from './icons/ChevronRightIcon.vue';
import ChevronLeftIcon from './icons/ChevronLeftIcon.vue';
import { resolveTopLevelMenuItems } from '../types/mainNav';
import type { MainNav } from '../types/mainNav';
import MenuIcon from '~icons/mdi/menu';
import CloseIcon from '~icons/mdi/close';
import {
  getDesktopNavStyle,
  getLogoTheme,
  getNavGridBaseStyle,
  getThemeBgClass,
  LIGHT_HEADER_THEME_STATE,
  DEFAULT_HEADER_THEME_STATE,
} from '../composables/useHeaderThemeDetection';
import type { HeaderThemeState } from '../types/headerTypes';
import { locationAutocompleteKey } from '../injectionKeys';

const TGTGLogo = resolveComponent('TGTGLogo');
const hasTgtgLogo = typeof TGTGLogo !== 'string';

const locationApi = inject(locationAutocompleteKey, null);
const suggestions = computed(() => locationApi?.suggestions.value ?? []);
const loadingSuggestions = computed(() => locationApi?.loadingSuggestions.value ?? false);
const initialSearchValue = computed(() => locationApi?.initialSearchValue.value);

const headerThemeState = inject<Ref<HeaderThemeState>>('headerThemeState');
const themeState = computed(() => headerThemeState?.value ?? DEFAULT_HEADER_THEME_STATE);

const props = defineProps<{
  isOpen: boolean;
  menuContent: null | MainNav;
  openMenu: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
  close: [];
}>();

const MOBILE_PANEL_ID = 'new-mobile-nav-panel';
const { t } = useI18n();

const isDesktop = useIsDesktop();
const isTablet = useIsTablet();
const isNotDesktop = computed(() => !isDesktop.value);
const secondLevelMobileMenu = ref(false);
const selectedMenuItemIndex = ref(0);

const mainMenuContent = computed(() => resolveTopLevelMenuItems(props.menuContent));
const selectedMenu = computed(() => mainMenuContent.value?.[selectedMenuItemIndex.value]);
const effectiveThemeState = computed(() =>
  props.openMenu ? LIGHT_HEADER_THEME_STATE : themeState.value,
);
const mobileLogoTheme = computed(() =>
  props.openMenu ? THEME.DARK : getLogoTheme(themeState.value),
);
const closeMenu = () => emit('close');
const toggleMenu = () => emit('toggle');

function selectTopLevelItem(index: number) {
  selectedMenuItemIndex.value = index;
  secondLevelMobileMenu.value = true;
}

watch([isDesktop, () => props.isOpen], () => {
  secondLevelMobileMenu.value = false;
});

const actions = computed(() => props.menuContent?.menuActions ?? []);
const mobileKey = computed(() => props.menuContent?.mobileHeaderActionKey);
const mobileHeaderAction = computed(() =>
  mobileKey.value ? actions.value.find(a => a._key === mobileKey.value) ?? null : null,
);
const menuFooterActions = computed(() =>
  mobileHeaderAction.value
    ? actions.value.filter(a => a._key !== mobileHeaderAction.value!._key)
    : actions.value,
);
const footerActionsProps = computed(() => ({
  variant: 'footer' as const,
  navActions: menuFooterActions.value,
  rootClass: 'nav-mobile__actions',
  contentTheme: (effectiveThemeState.value.isLightTheme ? 'light' : 'dark') as 'light' | 'dark',
  linkClass: 'nav-mobile__actions__link',
  buttonGroupClass: 'nav-mobile__actions__buttons',
}));

const showHeaderActions = computed(
  () => (isTablet.value && actions.value.length > 0) || !!mobileHeaderAction.value,
);
const headerActionsProps = computed(() =>
  isTablet.value
    ? { navActions: actions.value, mobileHeaderActionKey: undefined as string | undefined }
    : {
        navActions: mobileHeaderAction.value ? [mobileHeaderAction.value] : [],
        mobileHeaderActionKey: props.menuContent?.mobileHeaderActionKey,
      },
);

const showFooterActions = computed(
  () => !isTablet.value && menuFooterActions.value.length > 0,
);

const headerStyle = computed(() =>
  props.openMenu
    ? getNavGridBaseStyle(effectiveThemeState.value)
    : getDesktopNavStyle(effectiveThemeState.value, false),
);

const contentStyle = computed(() => ({
  ...getNavGridBaseStyle(effectiveThemeState.value),
  backgroundColor: 'transparent',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
}));

const unifiedBackdropStyle = computed(() =>
  props.isOpen && isNotDesktop.value ? effectiveThemeState.value.backgroundCssOpenMenu : undefined,
);

const contentClass = computed(() => getThemeBgClass(effectiveThemeState.value, 'nav-mobile__content'));

const handleSelectedOption = (value: Origin | undefined) => {
  if (value != null && locationApi) {
    locationApi.handleLocationSelected(value, 'header');
  }
};
</script>

<template>
  <DialogRoot
    :open="isOpen"
    @update:open="(open) => { if (open === false) closeMenu(); }"
  >
    <div
      class="nav-mobile__layout"
      :class="{ 'nav-mobile__layout--open': isOpen && isNotDesktop }"
    >
      <div
        v-if="isOpen && isNotDesktop"
        class="nav-mobile__unified-backdrop"
        :style="unifiedBackdropStyle"
        aria-hidden="true"
      />
      <nav class="nav-mobile" aria-label="Main mobile and tablet navigation">
        <div :class="cn('nav-mobile__header')" :data-analytics-click-behaviour="AnalyticsClickBehaviour.MobileNav" :style="headerStyle">
          <div class="nav-mobile__header__logo-wrapper amp-auto-track" :data-analytics-click-behaviour="AnalyticsClickBehaviour.HeaderLogo">
            <component
              :is="TGTGLogo"
              v-if="hasTgtgLogo"
              header-logo
              :theme="mobileLogoTheme"
            />
          </div>
          <div v-if="showHeaderActions" class="nav-mobile__header__actions">
            <HeaderActions
              :nav-actions="headerActionsProps.navActions"
              :mobile-header-action-key="headerActionsProps.mobileHeaderActionKey"
              :text-color="effectiveThemeState.textColor"
              :content-theme="effectiveThemeState.isLightTheme ? 'light' : 'dark'"
            />
          </div>
          <!-- [html-validate-disable-next no-missing-references] -->
          <button
            type="button"
            class="nav-mobile__header__button amp-auto-track"
            :aria-expanded="isOpen"
            :aria-controls="MOBILE_PANEL_ID"
            :aria-label="isOpen ? 'Close menu' : 'Open menu'"
            @click="isOpen ? closeMenu() : toggleMenu()"
          >
            <CloseIcon v-if="isOpen" class="w-full h-auto" />
            <MenuIcon v-else class="w-full h-auto" />
          </button>
        </div>
      </nav>

      <DialogContent
        v-if="isOpen && isNotDesktop"
        :id="MOBILE_PANEL_ID"
        class="nav-mobile__content"
        :class="contentClass"
        :style="contentStyle"
      >
        <DialogTitle class="sr-only">
          Mobile navigation
        </DialogTitle>
        <div v-if="locationApi" class="nav-mobile__content__finder">
          <Autocomplete
            :options="suggestions"
            :placeholder="t('headerAutocompletePlaceholder')"
            :loading="loadingSuggestions"
            :no-location-text="t('headerNoLocationText')"
            :header-autocomplete="false"
            :light-icon="effectiveThemeState.textColor?.includes('dark')"
            :initial-search-value="initialSearchValue"
            :border="true"
            @input-change="(v) => locationApi?.fetchSuggestions(v)"
            @selected="handleSelectedOption"
          />
        </div>
        <div v-if="!secondLevelMobileMenu" class="nav-mobile__content__wrapper">
          <div class="nav-mobile__content__inner">
            <div class="nav-mobile__content__scroll">
              <template v-for="(item, index) in mainMenuContent" :key="item.title">
                <CustomLink
                  v-if="item.directLink"
                  :link="item.directLink"
                  :title="item.title"
                  :new-window="item.newWindow"
                  class="flex w-full nav-mobile__content__main-link"
                >
                  <p class="nav-mobile__content__main-title">
                    {{ item.title }}
                  </p>
                  <ChevronRightIcon class="pt-8" />
                </CustomLink>
                <button v-else class="flex w-full" @click="selectTopLevelItem(index)">
                  <p class="nav-mobile__content__main-title">
                    {{ item.title }}
                  </p>
                  <ChevronRightIcon class="pt-8" />
                </button>
              </template>
            </div>
            <div class="nav-mobile__content__actions">
              <HeaderActions v-if="showFooterActions" v-bind="footerActionsProps" />
              <div class="nav-mobile__content__language-picker">
                <LanguagePicker
                  content-side="top"
                  content-class="nav-mobile__language-picker-content"
                  :content-side-offset="8"
                  backdrop
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="secondLevelMobileMenu" class="nav-mobile__content__wrapper">
          <div class="nav-mobile__content__inner">
            <div class="nav-mobile__content__scroll">
              <button class="flex w-full" @click="secondLevelMobileMenu = false">
                <ChevronLeftIcon class="pt-8 pr-8 ml-[-10px]" />
                <p class="nav-mobile__content__main-title">
                  {{ selectedMenu?.title }}
                </p>
              </button>
              <div v-for="list in selectedMenu?.submenuLists" :key="list.submenuListTitle">
                <h3 class="nav-mobile__content__list-title">
                  {{ list.submenuListTitle }}
                </h3>
                <NavSubmenuList :list="list" />
              </div>
              <div
                v-if="selectedMenu?.additionalInfo?.length"
                class="nav-mobile__content__additional-info"
                :aria-label="`Additional content for ${selectedMenu?.title}`"
              >
                <PortableEditor :content="selectedMenu?.additionalInfo ?? []" class="nav__additional-info" />
              </div>
            </div>
            <div class="nav-mobile__content__actions">
              <HeaderActions v-if="showFooterActions" v-bind="footerActionsProps" />
              <div class="nav-mobile__content__language-picker">
                <LanguagePicker
                  content-side="top"
                  content-class="nav-mobile__language-picker-content"
                  :content-side-offset="8"
                  backdrop
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </div>
  </DialogRoot>
</template>

<style>
.nav-mobile__content__main-link {
  color: inherit;
  text-decoration: none;
}
</style>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.nav-mobile__layout {
  position: relative;
}

.nav-mobile__layout--open {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
}

.nav-mobile__unified-backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}

.nav-mobile__layout--open .nav-mobile,
.nav-mobile__layout--open :deep(.nav-mobile__content) {
  position: relative;
  z-index: 1;

}

.nav-mobile {
  flex-shrink: 0;

  &__header {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    padding: theme('spacing.16') theme('spacing.16');

    &__actions {
      flex: 1;
      display: flex;
      justify-content: center;
      width: 100%;

      @screen tablet-wide {
        padding-right: theme('spacing.24');
        justify-content: flex-end;
      }

      @screen desk {
        padding-right: theme('spacing.0');
      }
    }

    &__button {
      width: 2.2rem;
      height: 2.2rem;
    }

    &__logo-wrapper {
      width: 3.2rem;
      height: 3.2rem;
      position: relative;
    }
  }

  &__content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: theme('spacing.0') theme('spacing.20');

    &__actions {
      align-self: stretch;
      width: 100%;
      flex-shrink: 0;
      min-width: 0;
      border-top: 1px solid color-mix(in srgb, theme('colors.black') 20%, transparent);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: theme('spacing.16');
      padding-top: theme('spacing.16');
      margin-top: theme('spacing.20');
    }

    &__finder {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: theme('spacing.16') theme('spacing.0') theme('spacing.24') theme('spacing.0');
    }

    &__main-title {
      padding-bottom: theme('spacing.24');
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      min-height: 2em;
      font-weight: theme('fontWeight.bold');
      max-width: 100%;
      text-transform: lowercase;
      padding-right: theme('spacing.8');

      &::first-letter {
        text-transform: uppercase;
      }
    }

    &__list-title {
      font-size: theme('fontSize.16');
      font-weight: theme('fontWeight.bold');
      margin: theme('spacing.16') 0 theme('spacing.8');

      .nav-mobile__content__scroll > div:not(:first-of-type) & {
        margin-top: theme('spacing.32');
      }
    }

    &__additional-info {
      margin-top: theme('spacing.24');
      padding-top: theme('spacing.24');
      border-top: 1px solid color-mix(in srgb, theme('colors.black') 20%, transparent);
    }

    &__wrapper {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }

    &__inner {
      min-height: 100%;
      display: flex;
      flex-direction: column;
    }

    &__scroll {
      flex: 1;
      min-height: 0;
      font-size: theme('fontSize.30');
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      padding: 2px;
    }

    &__language-picker {
      width: 100%;

      &-content {
        max-height: min(50vh, 368px);
      }
    }
  }
}

:deep(.nav-mobile__actions) {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: theme('spacing.8');
  font-size: theme('fontSize.14');
  line-height: 1.5;

  &__link {
    color: inherit;
  }

  &__link:hover {
    text-decoration: underline;
  }

  &--light {
    color: theme('colors.neutral.70');
  }

  &--dark {
    color: theme('colors.neutral.50');
  }

  &__buttons {
    font-size: theme('fontSize.14');
  }
}
</style>
